import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import "../styles/Report.css";

function Report() {

  const navigate = useNavigate();
  const audioRef = useRef(null);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [agentNumber, setAgentNumber] = useState("");
  const [customerNumber, setCustomerNumber] = useState("");

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [currentAudio, setCurrentAudio] = useState("");
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);

  const [dateError, setDateError] = useState("");

  const rowsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);

  const userName = sessionStorage.getItem("UserName");

  // Get today's date in YYYY-MM-DD format for max date attribute
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const todayDate = getTodayDate();

  useEffect(() => {
    if (!userName) {
      navigate("/");
    }
  }, []);

  const handleLogout = () => {
    sessionStorage.clear();
    navigate("/");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return dateString.split("T")[0];
  };

  const calculateEndTime = (time, ivrDuration) => {

    if (!time || !ivrDuration || ivrDuration === "0") return time;

    try {

      const [hours, minutes, seconds] = time.split(":").map(Number);

      const date = new Date();

      date.setHours(hours, minutes, seconds);

      date.setSeconds(date.getSeconds() + parseInt(ivrDuration));

      return date.toTimeString().split(" ")[0];

    } catch {
      return time;
    }

  };

  const validateDates = (from, to) => {
    // Check if dates are selected
    if (!from || !to) {
      alert("Both From Date and To Date are required");
      return false;
    }

    // Check if from date is greater than to date
    if (from > to) {
      alert("From Date cannot be greater than To Date");
      return false;
    }

    // Check if from date is in the future
    if (from > todayDate) {
      alert("From Date cannot be in the future");
      return false;
    }

    // Check if to date is in the future
    if (to > todayDate) {
      alert("To Date cannot be in the future");
      return false;
    }

    return true;
  };

  const handleFromDateChange = (e) => {
    const newFromDate = e.target.value;
    setFromDate(newFromDate);
    
    // Clear any previous error
    setDateError("");
  };

  const handleToDateChange = (e) => {
    const newToDate = e.target.value;
    setToDate(newToDate);
    
    // Clear any previous error
    setDateError("");
  };

  const fetchReport = async () => {

    // Validate dates before fetching
    if (!validateDates(fromDate, toDate)) {
      return;
    }

    try {

      let url = "/api/Master/GetClickToCallDetails";

      const params = new URLSearchParams();

      if (agentNumber) params.append("AgentNumber", agentNumber);
      if (fromDate) params.append("FromDate", fromDate);
      if (toDate) params.append("ToDate", toDate);
      if (customerNumber) params.append("CustomerNumber", customerNumber);

      const res = await api.get(`${url}?${params.toString()}`);

      const fetched = res.data || [];

      const enrichedData = fetched.map((item, index) => ({

        ...item,

        SNo: index + 1,

        Date: formatDate(item.Date),

        IvrDuration: item.IvrDuration || "0",

        CallDuration: item.CallDuration || "0",

        EndTime: calculateEndTime(item.Time, item.IvrDuration)

      }));

      setData(enrichedData);

      setFilteredData(enrichedData);

      setCurrentPage(1);
      
      // Clear any previous errors on successful fetch
      setDateError("");

    } catch (err) {

      console.error(err);

      alert("No Data Found");

    }

  };

  const playAudio = (url, index) => {
    // Stop current audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    setCurrentAudio(url);
    setCurrentlyPlaying(index);
    
    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.play();
      }
    }, 200);
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setCurrentAudio("");
    setCurrentlyPlaying(null);
  };

  const downloadCSV = () => {

    if (filteredData.length === 0) {
      alert("No data to download");
      return;
    }

    // Define headers in the desired order with S.No first
    const headers = [
      "S.No",
      "Agent Number",
      "Customer Number",
      "Date",
      "Start Time",
      "End Time",
      "IVR Duration",
      "Call Duration",
      "Call Status",
      "State",
      "Recording URL"
    ];

    const csvRows = [];
    csvRows.push(headers.join(","));

    filteredData.forEach(row => {
      // Map values according to the headers order
      const values = [
        row.SNo || "",
        row.AgentNumber || "",
        row.CallerNumber || "",
        row.Date || "",
        row.Time || "",
        row.EndTime || "",
        row.IvrDuration || "",
        row.CallDuration || "",
        row.CallStatus || "",
        row.State || "",
        row.RecordingURL || ""
      ].map(value => `"${value}"`); // Wrap in quotes to handle commas in values

      csvRows.push(values.join(","));
    });

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Call_Report.csv";
    a.click();
    window.URL.revokeObjectURL(url);

  };

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  return (

    <div className="report-container">

      <div className="report-header">

        <div className="header-top">

          <h2>Voice Panel Report</h2>

          <div className="header-right">

            <span className="welcome-text">
              Welcome {userName}
            </span>

            <button className="logout-btn" onClick={handleLogout}>
              Logout
            </button>

          </div>

        </div>

        <div className="filter-row">

          <label>From Date</label>

          <input
            type="date"
            value={fromDate}
            onChange={handleFromDateChange}
            max={todayDate}
          />

          <label>To Date</label>

          <input
            type="date"
            value={toDate}
            onChange={handleToDateChange}
            max={todayDate}
          />

          <label>Agent Number</label>

          <input
            type="text"
            value={agentNumber}
            onChange={(e)=>setAgentNumber(e.target.value)}
            placeholder="Enter agent number"
          />

          <label>Customer Number</label>

          <input
            type="text"
            value={customerNumber}
            onChange={(e)=>setCustomerNumber(e.target.value)}
            placeholder="Enter customer number"
          />

          <button className="search-btn" onClick={fetchReport}>
            Search
          </button>

          <button className="download-btn" onClick={downloadCSV}>
            Download CSV
          </button>

        </div>

      </div>

      <div className="table-container">

        <table>

          <thead>

            <tr>

              <th>S.No</th>
              <th>Agent Number</th>
              <th>Customer Number</th>
              <th>Date</th>
              <th>Start Time</th>
              <th>End Time</th>
              <th>IVR Duration</th>
              <th>Call Duration</th>
              <th>Call Status</th>
              <th>State</th>
              <th>Recording</th>

            </tr>

          </thead>

          <tbody>

            {currentRows.length > 0 ? (

              currentRows.map((row, index) => {

                const actualIndex = indexOfFirstRow + index;
                
                return (

                  <tr key={actualIndex}>

                    <td>{row.SNo}</td>
                    <td>{row.AgentNumber || "N/A"}</td>
                    <td>{row.CallerNumber || "N/A"}</td>
                    <td>{row.Date}</td>
                    <td>{row.Time}</td>
                    <td>{row.EndTime}</td>
                    <td>{row.IvrDuration}</td>
                    <td>{row.CallDuration}</td>
                    <td>{row.CallStatus}</td>
                    <td>{row.State}</td>

                    <td>

                      {row.RecordingURL ? (

                        <div className="recording-cell">
                          {currentlyPlaying === actualIndex ? (
                            <div className="audio-controls">
                              <audio 
                                ref={audioRef} 
                                src={currentAudio} 
                                controls 
                                onEnded={() => setCurrentlyPlaying(null)}
                              />
                              {/* <button 
                                className="stop-btn"
                                onClick={stopAudio}
                              >
                                ⏹ Stop
                              </button> */}
                            </div>
                          ) : (
                            <button
                              className="play-btn"
                              onClick={() => playAudio(row.RecordingURL, actualIndex)}
                            >
                              ▶ Play
                            </button>
                          )}
                        </div>

                      ) : "N/A"}

                    </td>

                  </tr>

                )}

            )) : (

              <tr>

                <td colSpan="11" style={{ textAlign: "center" }}>
                  No Data Found
                </td>

              </tr>

            )}

          </tbody>

        </table>

      </div>

      {filteredData.length > rowsPerPage && (

        <div className="pagination">

          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Prev
          </button>

          <span>
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>

        </div>

      )}

    </div>

  );

}

export default Report;